import React, { useMemo, useState } from 'react';
import { Card, Table, Button, Form, Badge, Row, Col } from 'react-bootstrap';
import moment from 'moment';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';

const ACCENT = '#60A5FA';

const FREQUENCY_LABELS = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
};

const toMonthly = (amount, frequency) => {
  const n = Number(amount) || 0;
  switch (frequency) {
    case 'daily':
      return n * 30;
    case 'weekly':
      return n * (52 / 12);
    case 'monthly':
      return n;
    case 'quarterly':
      return n / 3;
    case 'yearly':
      return n / 12;
    default:
      return n;
  }
};

const getNextDueDate = (subscription) => {
  if (!subscription.start_date) return null;
  const nextDue = moment(subscription.start_date);
  const end = subscription.end_date ? moment(subscription.end_date) : null;
  while (nextDue.isBefore(moment(), 'day')) {
    switch (subscription.frequency) {
      case 'daily':
        nextDue.add(1, 'days');
        break;
      case 'weekly':
        nextDue.add(1, 'weeks');
        break;
      case 'quarterly':
        nextDue.add(3, 'months');
        break;
      case 'yearly':
        nextDue.add(1, 'years');
        break;
      default:
        nextDue.add(1, 'months');
        break;
    }
    if (end && nextDue.isAfter(end, 'day')) return null;
  }
  return nextDue;
};

const isIncoming = (sub) => sub.subscription_type === 'incoming';

const SubscriptionList = ({ subscriptions, onEdit, onDelete, onAdd }) => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const summary = useMemo(() => {
    const buckets = {
      incoming: { business: 0, personal: 0 },
      outgoing: { business: 0, personal: 0 },
    };
    subscriptions.forEach((sub) => {
      const cat = sub.category === 'personal' ? 'personal' : 'business';
      const side = isIncoming(sub) ? 'incoming' : 'outgoing';
      buckets[side][cat] += toMonthly(sub.amount, sub.frequency);
    });
    return {
      ...buckets,
      getTotal: buckets.incoming.business + buckets.incoming.personal,
      payTotal: buckets.outgoing.business + buckets.outgoing.personal,
    };
  }, [subscriptions]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return subscriptions
      .filter((sub) => {
        if (filter === 'incoming') return isIncoming(sub);
        if (filter === 'outgoing') return !isIncoming(sub);
        if (filter === 'business') return sub.category === 'business';
        if (filter === 'personal') return sub.category === 'personal';
        return true;
      })
      .filter((sub) => {
        if (!q) return true;
        return (
          (sub.name || '').toLowerCase().includes(q) ||
          (sub.company || '').toLowerCase().includes(q) ||
          (sub.description || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => toMonthly(b.amount, b.frequency) - toMonthly(a.amount, a.frequency));
  }, [subscriptions, filter, searchTerm]);

  return (
    <div>
      <Row className="mb-3 g-3">
        <Col md={6}>
          <Card className="bg-dark text-light border-secondary h-100">
            <Card.Body>
              <div style={{ color: '#9ca3af', fontSize: 13 }}>I get · per month</div>
              <h3 className="text-success mb-3">€{summary.getTotal.toFixed(2)}</h3>
              <div className="d-flex justify-content-between">
                <div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>Business</div>
                  <div style={{ fontWeight: 600 }}>€{summary.incoming.business.toFixed(2)}</div>
                </div>
                <div className="text-end">
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>Personal</div>
                  <div style={{ fontWeight: 600 }}>€{summary.incoming.personal.toFixed(2)}</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="bg-dark text-light border-secondary h-100">
            <Card.Body>
              <div style={{ color: '#9ca3af', fontSize: 13 }}>I pay · per month</div>
              <h3 className="text-danger mb-3">€{summary.payTotal.toFixed(2)}</h3>
              <div className="d-flex justify-content-between">
                <div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>Business</div>
                  <div style={{ fontWeight: 600 }}>€{summary.outgoing.business.toFixed(2)}</div>
                </div>
                <div className="text-end">
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>Personal</div>
                  <div style={{ fontWeight: 600 }}>€{summary.outgoing.personal.toFixed(2)}</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="bg-dark text-light border-secondary mb-3">
        <Card.Body className="py-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <div style={{ color: '#9ca3af', fontSize: 13 }}>Net recurring / month</div>
              <h4
                className="mb-0"
                style={{ color: summary.getTotal - summary.payTotal >= 0 ? '#22c55e' : '#f87171' }}
              >
                €{(summary.getTotal - summary.payTotal).toFixed(2)}
              </h4>
            </div>
            <div className="d-flex gap-4 flex-wrap small" style={{ color: '#d1d5db' }}>
              <span>Business pay €{summary.outgoing.business.toFixed(2)}</span>
              <span>Personal pay €{summary.outgoing.personal.toFixed(2)}</span>
              <span>Business get €{summary.incoming.business.toFixed(2)}</span>
              <span>Personal get €{summary.incoming.personal.toFixed(2)}</span>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="bg-dark text-light border-secondary">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2 bg-dark border-secondary">
          <div className="d-flex gap-2 flex-wrap">
            <Form.Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-dark text-light border-secondary"
              style={{ width: 'auto' }}
            >
              <option value="all">All</option>
              <option value="incoming">I get</option>
              <option value="outgoing">I pay</option>
              <option value="business">Business</option>
              <option value="personal">Personal</option>
            </Form.Select>
            <Form.Control
              type="text"
              placeholder="Search name or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-dark text-light border-secondary"
              style={{ width: 260 }}
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={onAdd}
            style={{ backgroundColor: ACCENT, borderColor: ACCENT }}
          >
            <FaPlus className="me-2" /> Add subscription
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover variant="dark" className="align-middle mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Direction</th>
                <th>Category</th>
                <th>Per charge</th>
                <th>Monthly</th>
                <th>Frequency</th>
                <th>Next due</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-4" style={{ color: '#9ca3af' }}>
                    No subscriptions yet. Add Netflix, rent, retainers — anything recurring.
                  </td>
                </tr>
              ) : (
                filtered.map((sub) => {
                  const next = getNextDueDate(sub);
                  const monthly = toMonthly(sub.amount, sub.frequency);
                  return (
                    <tr key={sub.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{sub.name || 'Untitled'}</div>
                        {sub.company ? (
                          <small style={{ color: '#9ca3af' }}>{sub.company}</small>
                        ) : null}
                      </td>
                      <td>
                        <Badge bg={isIncoming(sub) ? 'success' : 'danger'}>
                          {isIncoming(sub) ? 'I get' : 'I pay'}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={sub.category === 'business' ? 'primary' : 'secondary'}>
                          {sub.category === 'business' ? 'Business' : 'Personal'}
                        </Badge>
                      </td>
                      <td
                        style={{
                          color: isIncoming(sub) ? '#22c55e' : '#f87171',
                          fontWeight: 600,
                        }}
                      >
                        {isIncoming(sub) ? '+' : '−'}€{Number(sub.amount).toFixed(2)}
                      </td>
                      <td>€{monthly.toFixed(2)}</td>
                      <td>{FREQUENCY_LABELS[sub.frequency] || sub.frequency}</td>
                      <td>{next ? next.format('DD-MM-YYYY') : '—'}</td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          style={{ borderColor: ACCENT, color: ACCENT }}
                          onClick={() => onEdit(sub)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => onDelete(sub.id)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SubscriptionList;
